import { useCallback, useMemo } from 'react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import type { PasskeyAuthConfig, PasskeyAuthOptions } from '@digiwedge/ui-antd';
import { useIdp } from './IdpProvider.js';
import { isPasskeysEnabled } from './passkeyFeature.js';

type TokenTriple = {
  accessToken: string;
  refreshToken: string;
  jti: string;
};

type CookieAuthOk = { ok: true };

const hasTokenTriple = (value: unknown): value is TokenTriple => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record['accessToken'] === 'string' &&
    typeof record['refreshToken'] === 'string' &&
    typeof record['jti'] === 'string'
  );
};

const isCookieAuthOk = (value: unknown): value is CookieAuthOk => {
  if (!value || typeof value !== 'object') return false;
  return (value as Record<string, unknown>)['ok'] === true;
};

export function usePasskeyLogin(): PasskeyAuthConfig | null {
  const idp = useIdp();
  const storeLogin = useGlobalStore((s: GlobalState) => s.login);
  const setAuthenticated = useGlobalStore((s: GlobalState) => s.setAuthenticated);
  const enabled = isPasskeysEnabled();
  const supported = browserSupportsWebAuthn();

  const onAuthenticate = useCallback(
    async ({ email, remember }: PasskeyAuthOptions) => {
      const data = await idp.beginPasskeyAuthentication(email ? { email } : {});
      const challengeId = data.challengeId;
      const optionsJSON = data.optionsJSON ?? data.options;
      if (!optionsJSON) {
        throw new Error('Passkey authentication options missing from server response.');
      }

      const assertion = await startAuthentication({
        optionsJSON: optionsJSON as unknown as Parameters<
          typeof startAuthentication
        >[0]['optionsJSON'],
      });
      const result = await idp.verifyPasskeyAuthentication({
        challengeId,
        response: assertion,
      });

      const cookieOnly = idp.tokenDelivery === 'cookie';
      if (cookieOnly) {
        if (hasTokenTriple(result)) {
          storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
        } else if (isCookieAuthOk(result)) {
          setAuthenticated();
        }
      } else if (hasTokenTriple(result)) {
        storeLogin(result.accessToken, result.refreshToken, result.jti, remember);
      }

      return result;
    },
    [idp, setAuthenticated, storeLogin],
  );

  return useMemo(() => {
    if (!enabled) return null;
    return {
      onAuthenticate,
      label: 'Sign in with Passkey',
      helperText: supported
        ? 'Use a passkey stored on this device or a security key.'
        : 'Passkeys are not available on this device or browser.',
      isAvailable: supported,
    };
  }, [enabled, onAuthenticate, supported]);
}
